import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';
import { compareAlphaFirstThenNumericNames } from "../../utils/stringUtils";

import type { Asset, AssetGroup } from '../../models/asset';
import { buildAssetTreeIndex, filterAssetTree } from '../../services/assetTreeFilterService';
import { buildAssetTypeById, getVehicleTypeCounts } from '../../services/memoryAssetService';

import UptimeTagRow from './components/UptimeTagRow';
import UptimeSidebarHeader from './components/UptimeSidebarHeader';

import './styles.scss';

interface UptimeSidebarProps {
    allAssets: Asset[];
    allGroups: AssetGroup[];

    setAssetIds: (assetIds: string[]) => void;
}

const LIMIT = 20;

const LOCALIZED_TAG_NAME_IDS: Record<string, string> = {
	"field-campains-group": "intl-msg:assetsTree.assetsTree.fieldCampaignGroup",
	"ungruped": "intl-msg:assetsTree.assetsTree.ungrouped",
};

const UptimeSidebar = ({
    allAssets,
    allGroups,

    setAssetIds,
}: UptimeSidebarProps) => {
	const intl = useIntl();

    const [viewMode, setViewMode] = useState<'assets' | 'groups'>('assets');
    const [searchTerm, setSearchTerm] = useState('');
    const [offset, setOffset] = useState(0);

    const [selectedAllAssets, setSelectedAllAssets] = useState(true);
    const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());

    const assetTreeIndex = useMemo(() => buildAssetTreeIndex(allAssets), [allAssets]);
    const filteredAssetTree = useMemo(
        () => filterAssetTree(assetTreeIndex, allGroups, searchTerm, viewMode),
        [assetTreeIndex, allGroups, searchTerm, viewMode]
    );
    const visibleGroups = filteredAssetTree.groups.slice(0, offset + LIMIT);
    const hasMoreGroups = visibleGroups.length < filteredAssetTree.groups.length;

    const publishSelection = useCallback(
        (assetIds: Set<string>) => {
            setAssetIds(Array.from(assetIds));
        },
        [setAssetIds]
    );

    const handleTagAssetsSelect = useCallback(
        (tagAssetIds: string[], tagSelectedAssetIds: string[]) => {
            setSelectedAllAssets(false);
            const tagAssetIdSet = new Set(tagAssetIds);

            setSelectedAssetIds(previousSelection => {
                const nextSelection = new Set([...previousSelection].filter(assetId => !tagAssetIdSet.has(assetId)));
                tagSelectedAssetIds.forEach(assetId => nextSelection.add(assetId));
                publishSelection(nextSelection);
                return nextSelection;
            });
        },
        [publishSelection]
    );

    const handleSearchTermChange = (term: string) => {
        setOffset(0);
        setSearchTerm(term);
    };

    const handleViewModeChange = (mode: 'assets' | 'groups') => {
        setOffset(0);
        setViewMode(mode);
    };

    useEffect(() => {
        publishSelection(new Set());
    }, []);

    useEffect(() => {
        if (!selectedAllAssets || allAssets.length === 0) {
            return;
        }

        const allSelectedAssetIds = filteredAssetTree.assetIds;
        setSelectedAssetIds(allSelectedAssetIds);
        publishSelection(allSelectedAssetIds);
    }, [selectedAllAssets, allAssets.length, filteredAssetTree.assetIds, publishSelection]);

    const filteredSelectionState = useMemo(() => {
        let selectedFilteredCount = 0;

        for (const assetId of filteredAssetTree.assetIds) {
            if (selectedAssetIds.has(assetId)) {
                selectedFilteredCount++;
            }
        }

        const filteredCount = filteredAssetTree.assetIds.size;

        return {
            areAllFilteredAssetsSelected: filteredCount > 0 && selectedFilteredCount === filteredCount,
            areSomeFilteredAssetsSelected: selectedFilteredCount > 0 && selectedFilteredCount < filteredCount,
        };
    }, [filteredAssetTree.assetIds, selectedAssetIds]);

    const handleToggleSelectAll = () => {
        if (!filteredSelectionState.areAllFilteredAssetsSelected) {
            const filteredSelection = new Set(filteredAssetTree.assetIds);
            setSelectedAllAssets(!searchTerm && filteredSelection.size === allAssets.length);
            setSelectedAssetIds(filteredSelection);
            publishSelection(filteredSelection);
            return;
        }

        setSelectedAllAssets(false);
        setSelectedAssetIds(previousSelection => {
            const nextSelection = new Set(
                [...previousSelection].filter(assetId => !filteredAssetTree.assetIds.has(assetId))
            );
            publishSelection(nextSelection);
            return nextSelection;
        });
    };
    const assetTypeById = useMemo(() => buildAssetTypeById(allAssets), [allAssets]);

    const filteredSelectedAssetIds = useMemo(
        () => new Set([...filteredAssetTree.assetIds].filter(assetId => selectedAssetIds.has(assetId))),
        [filteredAssetTree.assetIds, selectedAssetIds]
    );

    const vehicleTypeCounts = useMemo(
        () => getVehicleTypeCounts(assetTypeById, filteredSelectedAssetIds),
        [assetTypeById, filteredSelectedAssetIds]
    );

    if (!allAssets?.length || !allGroups?.length) {
        return (
            <div className='height-100pct display-flex align-items-center justify-content-center'>
                <Spinner />
            </div>
        );
    }

    return (
        <div className='Tree' style={{ height: '100%' }}>
            <UptimeSidebarHeader
                viewMode={viewMode}
                vehicleTypeCounts={vehicleTypeCounts}
                areAllFilteredAssetsSelected={filteredSelectionState.areAllFilteredAssetsSelected}
                areSomeFilteredAssetsSelected={filteredSelectionState.areSomeFilteredAssetsSelected}
                onSearchTermChange={handleSearchTermChange}
                onViewModeChange={handleViewModeChange}
                onToggleSelectAll={handleToggleSelectAll}
            />
            <div className='TreeRoot user-select-none' style={{ height: 'calc(100% - 100px)', overflow: 'auto' }}>
                {visibleGroups.map(group => (
                    <UptimeTagRow
                        key={group.id}
                        tagId={group.id}
                        tagName={group.name}
                        assets={filteredAssetTree.assetsByGroupId.get(group.id) ?? []}
                        selectedAssetIds={selectedAssetIds}
                        onAssetsSelect={handleTagAssetsSelect}
                    />
                ))}

                {hasMoreGroups && (
                    <div
                        className='LoadMore cursor-pointer text-center py-8 text-color-primary'
                        onClick={() => setOffset(currentOffset => currentOffset + LIMIT)}
                    >
                        {intl.formatMessage({ id: 'intl-msg:assetsTree.loadMore' })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UptimeSidebar;
